import * as sentry from '@sentry/react';

const MAX_RETRIES = 4;
const RETRY_DELAY = 5000;

export const get = async (
  apiRoute: string,
  skipBaseUrl?: boolean,
  includeCredentials?: boolean,
  skipJson?: boolean,
  idToken?: string,
  callWithRetries?: boolean,
  params?: {[s: string]: any}
) => {
  let endPoint = apiRoute;

  const headers = new Headers();
  headers.append('Content-Type', 'application/json');
  headers.append('x-app-version', '2.2.0');
  if (includeCredentials) {
    headers.append('Authorization', `Bearer ${idToken}`);
  }

  const requestOptions: RequestInit = {
    method: 'GET',
    headers: headers
  };

  if (params) {
    const paramStr = Object.entries(params as any)
      .map((value) => value.join('='))
      .join('&');
    endPoint = `${endPoint}?${paramStr}`;
  }

  return callWithRetries
    ? await callFetchWithRetry(endPoint, requestOptions, skipJson)
    : await callFetch(endPoint, requestOptions, skipJson);
};

export const post = async (
  apiRoute: string,
  payload: any,
  skipBaseUrl?: boolean,
  skipStringify = false,
  appendHeaders = true,
  includeCredentials = false,
  idToken?: string,
  callWithRetries = false,
  throwOriginalError = false
) => {
  const endPoint = apiRoute;

  const headers = new Headers();
  if (appendHeaders) {
    headers.append('Content-Type', 'application/json');
    headers.append('x-app-version', '2.2.0');
  }
  if (includeCredentials) {
    headers.append('Authorization', `Bearer ${idToken}`);
  }
  const raw = skipStringify ? payload : JSON.stringify(payload || '{}');

  const requestOptions: RequestInit = {
    method: 'POST',
    headers: headers,
    body: raw
  };

  return callWithRetries
    ? await callFetchWithRetry(endPoint, requestOptions, undefined, throwOriginalError)
    : await callFetch(endPoint, requestOptions);
};

const callFetch = async (endPoint: RequestInfo, requestOptions: RequestInit, skipJson = false) => {
  try {
    const data = await fetch(endPoint, requestOptions);

    if (skipJson) return data;
    const response = await data.json();

    return response;
  } catch (err: any) {
    console.error('Error during request:', err);
    throw err;
  }
};

const callFetchWithRetry = async (
  endPoint: RequestInfo,
  requestOptions: RequestInit,
  skipJson = false,
  throwOriginalError?: boolean
) => {
  const successfulStatusCodes = [200, 201, 202, 203, 204, 205, 206];
  for (let retries = 1; retries <= MAX_RETRIES; retries++) {
    try {
      const data: any = await fetch(endPoint, requestOptions);
      if (data.statusCode === 408 || data.status === 408) {
        if (retries < MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY * retries));
          continue;
        }
      }
      if (
        !data.ok &&
        (!successfulStatusCodes.includes(data.statusCode) || !successfulStatusCodes.includes(data.status))
      ) {
        sentry.withScope((scope) => {
          scope.setContext('api-request-details', {
            route: endPoint,
            statusCode: data.statusCode || data.status,
            tok:
              (requestOptions?.headers as Headers)?.get('Authorization') ||
              (requestOptions?.headers as Headers)?.get('authorization'),
            retryCount: retries
          });
          scope.captureMessage('api-failure', 'error');
        });
        const errorResponse = await data?.json();
        if (throwOriginalError && errorResponse) {
          throw errorResponse;
        }
        throw new Error('Something went wrong');
      }
      if (skipJson) return data;
      const response = await data.json();

      return response;
    } catch (err) {
      if (retries >= MAX_RETRIES) {
        throw err;
      }
    }
  }
};

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
export type FetchRequestOptions = {
  idToken?: string;
  searchParams?: Record<string, string | number | boolean | undefined>;
  json?: any;
  body?: any;
  retries?: number;
  headers?: Record<string, string>;
};
export const fetchRequest = async <ResponseBody>(
  method: HttpMethod,
  path: string,
  options: FetchRequestOptions = {}
) => {
  const {idToken, searchParams, headers, retries = 0, body, json} = options;
  const calculatedHeaders = new Headers();
  if (headers) {
    for (const headerKey in headers) {
      calculatedHeaders.append(headerKey, headers[headerKey]);
    }
  }
  if (idToken) {
    calculatedHeaders.append('Authorization', `Bearer ${idToken}`);
  }
  const requestOptions: RequestInit = {
    method: method,
    headers: calculatedHeaders
  };
  if (json) {
    calculatedHeaders.append('Content-Type', 'application/json');
    requestOptions.body = JSON.stringify(json);
  }
  if (body) {
    requestOptions.body = body;
  }

  const paramStr = searchParams
    ? new URLSearchParams(
        Object.entries(searchParams).reduce(
          (acc, [key, value]) => {
            if (value === undefined) return acc;
            acc[key] = String(value);
            return acc;
          },
          {} as Record<string, string>
        )
      ).toString()
    : '';

  const requestUrl = paramStr ? `${path}?${paramStr}` : path;
  let requestRetry = retries + 1; // request = 1 + number of retry;
  while (requestRetry) {
    try {
      const response = await fetch(requestUrl, requestOptions);
      if (!response.ok) {
        throw response;
      } else {
        const data = await response.json();
        return data as ResponseBody;
      }
    } catch (e: any) {
      sentry.withScope((scope) => {
        scope.setContext('api-request-details', {
          route: requestUrl,
          statusCode: e.statusCode || e.status,
          tok:
            (requestOptions?.headers as Headers)?.get('Authorization') ||
            (requestOptions?.headers as Headers)?.get('authorization'),
          retryCount: retries
        });
        scope.captureMessage('api-failure', 'error');
      });
      requestRetry--;
    }
  }
  throw new Error('Request failed');
};
